import { getLinkForCourseLesson } from "../../../../services/Utils";

import lesson0506 from "../lesson-06/lesson";
import lesson0511 from "../lesson-11/lesson";

const linkLesson0506 = getLinkForCourseLesson("53e2fab0-edf6-4634-a20e-ae0cdfe73cba", lesson0506.id);
const linkLesson0511 = getLinkForCourseLesson("53e2fab0-edf6-4634-a20e-ae0cdfe73cba", lesson0511.id);

const theory = ` <h3 class=sectionHead>What will you learn in this lesson?</h3>
<p>In this lesson you will learn that the amount of absorption in the receiving room has an influence on the overall sound insulation between two rooms. This influence is often negligible, but in receiving rooms with very little absorption, there can be a reduction in the sound insulation of several dB, and sometimes even more than 5 dB. You will learn how the reverberant field in the receiving room plays a key role in this.
<h3 class=sectionHead>Introduction</h3>
<p>Until this point in the course you were taught that room acoustics and building acoustics are two completely separate subjects. It is indeed very important to understand the fundamental differences between the two and to be aware of their underlying physical principles. In very simple terms, room acoustics deals with absorption and reverberation within a single space, while building acoustics primarily deals with sound insulation between different spaces.<br class=newline>
<p>Real life, however, is often more complex than can be explained by a single theory. It turns out that room acoustics can in some cases have a significant effect on the achieved sound insulation. This is due to the reverberant field in the receiving room, which amplifies the local sound (see the Strength parameter G in <a href="${linkLesson0511}" target="_blank">Lesson 5.11</a>) and contributes to the total sound pressure level that can be several dB higher than initially expected.<br class=newline>
<p>On the one hand, it is important that future practitioners of acoustics are aware of this effect, and that they are able to quantify it in dB. On the other hand, architects and other designers should know that the influence of this effect on the overall sound insulation performance is ultimately small. Instead, their attention needs to be focused on the properties of separating building elements (their construction details and, ultimately, the values of the sound reduction index), which govern the direct transmission between two rooms. Special attention needs to be paid to the details of the junctions of building elements, which govern the flanking transmission of sound between spaces.<br class=newline>
<p>In other words: sound insulation problems are solved with appropriate design and construction of building elements and structures, and not by room acoustic interventions, i.e., by adding absorption into the receiving room, as the maximum obtainable improvement is typically limited to a few dB. Moreover, a perceptually significant improvement is manifested only when the acoustic treatment drastically changes the room acoustic conditions in a room. An example of such a dramatic change can be when a freshly constructed room with hard, reflective surfaces (concrete, plaster, etc.) is brought into its finished state through the process of interior design, which includes acoustic treatment.
<h3 class=sectionHead>Direct and reverberant sound fields in a single room</h3>
<p>
   Let us first take a step back and reconsider the case of a single room. In <a href="${linkLesson0506}" target="_blank">Lesson 5.6</a> you learned that the total sound field in a room consists of two components: the direct field and the reverberant field. Each of these two components contributes to the total sound pressure level. The direct sound pressure level (
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi mathvariant=italic>Lp</mi>
      <mo class=MathClass-punc>,</mo>
      <mi mathvariant=italic>dir</mi>
   </math>
   ) resulting from the direct field only depends on the properties of the sound source, namely, its sound power and directivity, and on the distance to the source. On the other hand, the reverberant sound pressure level (
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi mathvariant=italic>Lp</mi>
      <mo class=MathClass-punc>,</mo>
      <mi mathvariant=italic>rev</mi>
   </math>
   ) resulting from the reverberant field depends mainly on the total amount of sound absorption A in the room, and on the sound power of the source. The distance to the source has a minor influence on the reverberant sound pressure level and can be neglected in most small spaces.<br class=newline>
<p>For an omnidirectional source which emits sound equally in all directions, the sound pressure levels of direct and reverberant sound can be approximated by the following simple equations (see <a href="${linkLesson0506}" target="_blank">Lesson 5.6</a>):
<table class=equation>
   <tr>
      <td>
         <math display=block class=equation xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>L</mi>
               </mrow>
               <mrow>
                  <mi>p</mi>
                  <mo class=MathClass-punc>,</mo>
                  <mi mathvariant=italic>dir</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>L</mi>
               </mrow>
               <mrow>
                  <mi>W</mi>
               </mrow>
            </msub>
            <mi>–</mi>
            <mn>20</mn>
            <mi mathvariant=italic>log</mi>
            <mo class=MathClass-open>(</mo>
            <mi>r</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-bin>−</mo>
            <mn>11</mn>
         </math>
      <td class=eq-no>(1)
</table>
<table class=equation>
   <tr>
      <td>
         <math display=block class=equation xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>L</mi>
               </mrow>
               <mrow>
                  <mi>p</mi>
                  <mo class=MathClass-punc>,</mo>
                  <mi mathvariant=italic>rev</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>L</mi>
               </mrow>
               <mrow>
                  <mi>W</mi>
               </mrow>
            </msub>
            <mi>–</mi>
            <mn>10</mn>
            <mi mathvariant=italic>log</mi>
            <mo class=MathClass-open>(</mo>
            <mi>A</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-bin>+</mo>
            <mn>6</mn>
         </math>
      <td class=eq-no>(2)
</table>
<p>
   In the equations above,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi mathvariant=italic>Lw</mi>
   </math>
   represents the sound power level emitted by the source (also expressed in dB, but not to be confused with sound pressure), r is the distance to the source, and A is the total amount of absorption present in the room (also known as equivalent absorption area, expressed in metric Sabines).<br class=newline>
<p>
   The total sound pressure level (
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi mathvariant=italic>Lp</mi>
      <mo class=MathClass-punc>,</mo>
      <mi mathvariant=italic>tot</mi>
   </math>
   ) is the logarithmic sum of
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi mathvariant=italic>Lp</mi>
      <mo class=MathClass-punc>,</mo>
      <mi mathvariant=italic>dir</mi>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi mathvariant=italic>Lp</mi>
      <mo class=MathClass-punc>,</mo>
      <mi mathvariant=italic>rev</mi>
   </math>
   :
<table class=equation>
   <tr>
      <td>
         <math display=block class=equation xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>L</mi>
               </mrow>
               <mrow>
                  <mi>p</mi>
                  <mo class=MathClass-punc>,</mo>
                  <mi mathvariant=italic>tot</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mn>10</mn>
            <mi mathvariant=italic>log</mi>
            <mo class=MathClass-open>(</mo>
            <mn>1</mn>
            <msup>
               <mrow>
                  <mn>0</mn>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>L</mi>
                     </mrow>
                     <mrow>
                        <mi>p</mi>
                        <mo class=MathClass-punc>,</mo>
                        <mi mathvariant=italic>dir</mi>
                     </mrow>
                  </msub>
                  <mo class=MathClass-bin>∕</mo>
                  <mn>10</mn>
               </mrow>
            </msup>
            <mo class=MathClass-bin>+</mo>
            <mn>1</mn>
            <msup>
               <mrow>
                  <mn>0</mn>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>L</mi>
                     </mrow>
                     <mrow>
                        <mi>p</mi>
                        <mo class=MathClass-punc>,</mo>
                        <mi mathvariant=italic>rev</mi>
                     </mrow>
                  </msub>
                  <mo class=MathClass-bin>∕</mo>
                  <mn>10</mn>
               </mrow>
            </msup>
            <mo class=MathClass-close>)</mo>
         </math>
      <td class=eq-no>(3)
</table>
<p>This is what happens in a single room and is purely a matter of room acoustics.<br class=newline>
<p>What happens if we now extend this idea to the case of two adjacent rooms, and we want to know the sound insulation between them (which is a typical building acoustics question)?
<h3 class=sectionHead>Direct and reverberant fields in two adjacent rooms</h3>
<p>Let us now consider two adjacent rooms, labelled room 1 and room 2. If we put a sound source in room 1, we know that a portion of the sound will travel through the separating wall and arrive in room 2. But, at this point we can be more precise and say that portions of both the direct sound and the reverberant sound will travel through the separating wall. In addition, the sound that comes through the separating wall and ultimately ends up in room 2 will contribute to both the direct field and the reverberant field in that room. This means that it makes sense to consider four different contributions of sound travelling from room 1 to room 2 through the separating wall:
<ul class=itemize1>
   <li class=itemize><em><span class=cmti-10>dir – dir</span></em>: the contribution of the direct field in room 1 to the direct field in room 2
   <li class=itemize><em><span class=cmti-10>rev – rev</span></em>: the contribution of the reverberant field in room 1 to the reverberant field in room 2
   <li class=itemize><em><span class=cmti-10>dir – rev</span></em>: the contribution of the direct field in room 1 to the reverberant field in room 2
   <li class=itemize><em><span class=cmti-10>rev – dir</span></em>: the contribution of the reverberant field in room 1 to the direct field in room 2
</ul>
<p>For a precise calculation of the sound insulation (sound level difference (D)) between two rooms, these four contributions need to be taken into account separately and summed up (by linear superposition). The European Standard EN 12354 [1] defines and explains the calculation methods that should be used in this context. In practice, the total sound pressure level in a room is typically governed by the reverberant field. For this reason, the <em><span class=cmti-10>rev – rev</span></em> contribution is the one that will typically determine the sound insulation that can be achieved.<br class=newline>
<p>
   If
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
   </math>
   denotes the total sound pressure level in room 1 (the source room) and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msub>
   </math>
   is the total sound pressure level in room 2 (the receiving room), then D =
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
   </math>
   –
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msub>
   </math>
   is defined as the sound level difference between room 1 and room 2. For the reverberant-field-to-reverberant-field contribution, it can be shown that D can be approximated by the following equation:
<table class=equation>
   <tr>
      <td>
         <math display=block class=equation xmlns=http://www.w3.org/1998/Math/MathML>
            <mi>D</mi>
            <mo class=MathClass-rel>=</mo>
            <mi mathvariant=italic>R–</mi>
            <mn>10</mn>
            <mi mathvariant=italic>log</mi>
            <mo class=MathClass-open>(</mo>
            <mfrac>
               <mrow>
                  <mi>S</mi>
               </mrow>
               <mrow>
                  <mi>A</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-close>)</mo>
         </math>
      <td class=eq-no>(4)
</table>
<p>
   In the equation above, R is the sound reduction index of the separating wall element, S the surface area of the separating wall element (in
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>m</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msub>
   </math>
   ), and A is the equivalent absorption area in the receiving room. In diffuse field conditions, A can be easily derived from the Sabine equation for reverberation time
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>T</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>0</mn>
      <mo class=MathClass-punc>,</mo>
      <mn>161</mn>
      <mo class=MathClass-bin>∗</mo>
      <mi>V</mi>
      <mo class=MathClass-bin>∕</mo>
      <mi>A</mi>
   </math>
   as
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>A</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>0</mn>
      <mo class=MathClass-punc>,</mo>
      <mn>161</mn>
      <mo class=MathClass-bin>∗</mo>
      <mi>V</mi>
      <mo class=MathClass-bin>∕</mo>
      <mi>T</mi>
   </math>
   , once the volume of the room V has been calculated and the reverberation time T has been measured or calculated.<br class=newline>
<p>From equation (4) we can firstly learn that the bigger the surface area of the separating element, the lower the sound level difference D becomes (and vice versa). This is simply because the separating element is, in fact, the sound source for the receiving room, and more sound energy is transmitted to the receiving room if this source is larger (doubling the surface area doubles the transmitted sound energy). Secondly, more absorption in the receiving room leads not only to a shorter reverberation time, but also to a lower reverberant sound pressure level and, consequently, to a higher sound level difference (and vice versa).<br class=newline>
<p>
   Please note that all parameters in the above equations (except the geometric ones S, V and r) are frequency-dependent. Therefore, the calculation should be done in each frequency band separately (octave bands or 1/3 octave bands). Only at the end should the “w” frequency weighting be applied in order to arrive at the single number rating
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi mathvariant=italic>Dw</mi>
   </math>
   .<br class=newline>
<p>In addition, please note that the above reasoning was done purely for the sound travelling through the separating wall itself, i.e. for direct transmission. In addition to the direct path, there are many flanking transmission paths, and each of them has four direct/reverberant contributions of its own. More information on this can be found in different parts of the European Standard EN 12354 [<span class=cmbx-10>1</span>]. Software packages such as Acoubat, Bastian or SONarchitect take into account not only the different (direct and flanking) transmission paths between rooms, but also the direct and reverberant components in each room.<br class=newline>
<p>By the way, you will have noticed that in this lesson the word “direct” was used for two different physical notions in two very different meanings. The first one is <em><span class=cmti-10>direct</span></em> sound field (as opposed to reverberant sound field) used in room acoustic context for sound coming straight from the sound source. The second one is <em><span class=cmti-10>direct</span></em> transmission (as opposed to flanking transmission) used in building acoustic context to denote the sound transmission straight through a building element. Do not let yourself be confused by this. If you have not fully understood these concepts yet, do not worry. Just reread certain parts of this course and/or continue learning from other knowledge sources if you want to master the subject of acoustics.
<h3 class=sectionHead>Example of sound insulation between two rooms</h3>
<p>
   The principle part of this lesson, shown on the right hand side, gives you an example of two adjacent rooms having equal volume, with a sound source placed in room 1 (the source room). The reverberant sound pressure level is shown in both rooms (
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msub>
   </math>
   respectively), as well as the sound insulation or sound level difference (D). You can see what happens when sound absorption is added into one of the rooms or both of them, while the sound power of the source and the sound reduction index of the wall are kept constant in all cases.<br class=newline>
<p>With no additional absorption present in either room (only residual absorption from the reflective walls, ceiling and floor), the reverberation time will be long in both rooms (in this case 2 seconds). In this case, the lack of absorption in the source room leads to a high reverberant level in the source room. However, this is the property of the source (if the entire room is viewed as the source) and has no influence on the sound insulation properties of the separating wall towards the receiving room. On the other hand, the lack of absorption in the receiving room leads to a high reverberant level in this room. Due to this, the sound insulation between the source room and the receiving room will be reduced to the level difference of D = 36 dB, even though the sound reduction index of the separating wall is R = 40 dB.<br class=newline>
<p>With a significant amount of absorption added into the source room, the reverberation time in the source room will be shortened, in this case by a factor of 4, and the reverberant level will be reduced, in this case by 6 dB. This intervention does not change the sound insulation (expressed with sound level difference (D)) between the rooms in any way. Instead, the intervention has been made on the source side itself by reducing the reverberant level in the source room. As there is less sound energy to be transmitted to the receiving room to begin with, the sound pressure level in the receiving room will be reduced by the same amount. As always, mitigating noise problems by intervening at or near the source is a good idea, because it reduces the noise that can be transmitted in the first place.<br class=newline>
<p>With a significant amount of absorption added into the receiving room, the reverberation time in the receiving room will be shortened, and the reverberant level will be reduced. The situation in the source room is the same as initially presented, as no interventions have been made. In this example, the reverberation time in the receiving room has been shortened by a factor of 4, and the reverberant level has been reduced by 6 dB. (From a purely room acoustical point of view, another way of saying this is that the strength factor G in the receiving room has been reduced by 6 dB.) Compared with the case with no absorption in either room, we have the same source conditions in both cases, and we end up with the sound pressure level in the receiving room that is 6 dB lower. This means that the sound insulation between the source room and the receiving room has been increased by 6 dB, from D = 36 dB to D = 42 dB.<br class=newline>
<p>Finally, with absorption added into both rooms, we are intervening at the source, thus reducing the reverberant level in the source room by 6 dB, and reducing the amount of sound energy to be transmitted to the receiving room. Simultaneously, we are intervening in the receiving room, reducing the reverberant level there, which automatically increases the sound insulation between the rooms, expressed by the sound level difference (D), by 6 dB as well. This is the best-case scenario, as it results in the lowest sound pressure level in the receiving room as the space we aim to protect from noise coming from the source room.<br class=newline>
<p>
   As a closing remark, you may wonder why we are talking about the “raw” sound level difference
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>D</mi>
   </math>
   =
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
   </math>
   –
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msub>
   </math>
   in this example, and not the standardised sound level difference
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi mathvariant=italic>DnT</mi>
   </math>
   (or
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi mathvariant=italic>DnT</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>w</mi>
   </math>
   after frequency weighting) that is often found in standards. The reason is simply that
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi mathvariant=italic>DnT</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>w</mi>
   </math>
   was mainly invented to be able to carry out measurements in unfurnished or unfinished rooms and correct for the missing absorption, or more generally to compare measurements for different constructions with the room acoustic effect compensated for. However, in real life, the room acoustic effect does exist, and if the reverberation in the finished room is pronounced due to a low equivalent absorption area A, the user will ultimately perceive D (or
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi mathvariant=italic>Dw</mi>
   </math>
   as a frequency-weighted single-number value) as a correct measure of sound insulation performance.
<p>
<h3 class=likesectionHead>Glossary</h3>
<dl>
  <dt>
    <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
        <mi mathvariant=italic>Dw</mi>
    </math>
  <dd>weighted sound level difference.
  <dt>
    <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
        <mi mathvariant=italic>DnT</mi>
        <mo class=MathClass-punc>,</mo>
        <mi>w</mi>
    </math>
  <dd>standardised weighted sound level difference.
  <dt>
    <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
        <mi mathvariant=italic>DnT</mi>
    </math>
  <dd>standardised sound level difference.
  <dt>
    <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
        <mi mathvariant=italic>Lw</mi>
    </math>
  <dd>sound power level.
  <dt>
    <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
        <mi mathvariant=italic>Lp</mi>
        <mo class=MathClass-punc>,</mo>
        <mi mathvariant=italic>dir</mi>
    </math>
  <dd>direct sound pressure level.
  <dt>
    <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
        <mi mathvariant=italic>Lp</mi>
        <mo class=MathClass-punc>,</mo>
        <mi mathvariant=italic>rev</mi>
    </math>
  <dd>reverberant sound pressure level.
  <dt>
    <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
        <mi mathvariant=italic>Lp</mi>
        <mo class=MathClass-punc>,</mo>
        <mi mathvariant=italic>tot</mi>
    </math>
  <dd>total sound pressure level
  <dt>A
  <dd>equivalent absorption area
  <dt>D
  <dd>sound level difference
  <dt>G
  <dd>strength
  <dt>R
  <dd>sound reduction index
  <dt>r
  <dd>distance to the source
  <dt>S
  <dd>surface area of the separating element
  <dt>T
  <dd>reverberation time
  <dt>V
  <dd>volume of the room
</dl>
<h3 class=sectionHead>References</h3>
<dl class=thebibliography>
   <dt id=X0-EN12354 class=thebibliography>[1]
   <dd id=bib-1 class=thebibliography>
      <p><span class=cmti-10>EN ISO 12354-1 : 2000 - Estimation of acoustic performance of buildings </span><span class=cmti-10>from the performance of elements - Part 1 : Airborne sound insulation </span><span class=cmti-10>between rooms</span>. 2000.
</dl>`;

export default theory;
